/**
 * The ONLY navigable element the agent surface renders.
 *
 * ══ WHY A DEEP LINK IS THE HIGHEST-VALUE TRUST FEATURE HERE ════════════════
 *
 * Every `MetricDefinition` declares the existing admin page that already shows
 * its number. Rendering it turns the chat into a FRONT DOOR to numbers the team
 * already trusts, instead of a parallel universe with its own figures — and it
 * costs one component. `tests/unit/lint/agent-metric-deeplinks-exist.test.ts`
 * asserts every declared `href` resolves to a real route, because a dead deep
 * link is worse than none: it teaches the operator that the citations are
 * decorative.
 *
 * ══ AND WHY IT GOES THROUGH AN ENUMERATED ALLOWLIST ════════════════════════
 *
 * 🔴 `safeRoute` is EXACT MATCH against a frozen list, not a URL parser. The
 * exfiltration class this design is built against — EchoLeak (CVE-2025-32711),
 * GrafanaGhost, Superhuman — twice defeated a URL ALLOWLIST that tried to
 * reason about a string. A value that is not literally one of ten known admin
 * routes renders as TEXT, and there is no branch here that can produce an
 * external destination.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { safeRoute } from '../answer-view';

export function DeepLink({
  to,
  label,
  className,
}: {
  to: string | null | undefined;
  label: string;
  className?: string;
}) {
  const route = safeRoute(to);
  if (route === null) {
    // Not an error and not hidden: the operator still learns which page owns
    // this number, they just do not get a click.
    return <span className={className}>{label}</span>;
  }
  return (
    <Link
      to={route}
      className={
        className ??
        'inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline'
      }
    >
      {label}
      <ArrowRight className="h-3 w-3" aria-hidden />
    </Link>
  );
}
